import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Truck, ShieldCheck, BadgeCheck, Store, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Stars } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Textarea } from "@/components/ui/textarea";

export default function ProductPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [wished, setWished] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", text: "" });

  const load = async () => {
    const { data } = await api.get(`/products/${id}`);
    setProduct(data);
    setSelectedOffer(data.featured_offer);
    // recently viewed (local snapshot for cold-start personalization)
    try {
      const card = {
        id: data.id, title: data.title, brand: data.brand, image: data.images?.[0],
        rating: data.rating, review_count: data.review_count,
        price: data.featured_offer?.price, mrp: data.featured_offer?.mrp,
        discount_pct: data.featured_offer ? Math.round((1 - data.featured_offer.price / (data.featured_offer.mrp || data.featured_offer.price)) * 100) : 0,
        seller_name: data.featured_offer?.seller_name, featured_offer_id: data.featured_offer?.id,
      };
      const prev = JSON.parse(localStorage.getItem("vendora_recent") || "[]").filter((p) => p.id !== data.id);
      localStorage.setItem("vendora_recent", JSON.stringify([card, ...prev].slice(0, 8)));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load().catch(() => toast.error("Product not found"));
    api.get(`/products/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
    api.post("/track", { type: "product_view", product_id: id }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!product) return <div className="max-w-7xl mx-auto px-8 mt-12"><div className="h-[500px] rounded-2xl bg-slate-100 animate-pulse" /></div>;

  const offer = selectedOffer;
  const discount = offer ? Math.round((1 - offer.price / (offer.mrp || offer.price)) * 100) : 0;

  const addToCart = async (buyNow = false) => {
    if (!user) return nav("/login");
    if (!offer) return;
    try {
      await add(offer.id, 1);
      if (buyNow) nav("/checkout");
      else toast.success("Added to cart");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const toggleWish = async () => {
    if (!user) return nav("/login");
    const { data } = await api.post(`/wishlist/${product.id}`);
    setWished(data.added);
    toast.success(data.added ? "Saved to wishlist" : "Removed from wishlist");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/products/${product.id}/reviews`, reviewForm);
      toast.success("Review published");
      setReviewForm({ rating: 5, title: "", text: "" });
      const { data } = await api.get(`/products/${product.id}/reviews`);
      setReviews(data);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div data-testid="product-page" className="max-w-7xl mx-auto px-4 sm:px-8 mt-8">
      <p className="text-xs text-slate-400 mb-6">
        <Link to="/search" className="hover:text-blue-600">All products</Link>
        {" / "}
        <Link to={`/search?category=${encodeURIComponent(product.category_name)}`} className="hover:text-blue-600">{product.category_name}</Link>
      </p>
      <div className="grid md:grid-cols-2 gap-12">
        <div className="md:sticky md:top-32 self-start">
          <div className="rounded-xl overflow-hidden bg-slate-100 aspect-square">
            {product.images?.length ? (
              <img src={product.images[imgIdx]} alt={product.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300">No image</div>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((src, i) => (
                <button key={i} data-testid={`thumbnail-${i}`} onClick={() => setImgIdx(i)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === imgIdx ? "border-blue-500" : "border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">{product.brand}</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">{product.title}</h1>
          <div className="flex items-center gap-2 mt-3">
            <Stars rating={product.rating} className="w-4 h-4" />
            <span className="text-sm text-slate-500">{product.rating} · {product.review_count} reviews</span>
          </div>

          {offer ? (
            <div data-testid="buy-box" className="mt-6 rounded-2xl border border-slate-200 p-6">
              <div className="flex items-baseline gap-3">
                <span data-testid="product-price" className="text-3xl font-bold font-heading">{inr(offer.price)}</span>
                {offer.mrp > offer.price && <span className="text-slate-400 line-through">{inr(offer.mrp)}</span>}
                {discount > 0 && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">{discount}% off</span>}
              </div>
              <p className="text-xs text-slate-400 mt-1">Inclusive of all taxes</p>
              <div className="mt-4 space-y-2 text-sm">
                <p className="flex items-center gap-2 text-slate-600">
                  <Store className="w-4 h-4 text-slate-400" />
                  Sold by <Link to={`/store/${offer.seller_id}`} data-testid="seller-store-link" className="font-semibold text-slate-900 hover:text-blue-600 underline-offset-2 hover:underline">{offer.seller_name}</Link>
                  <span className="inline-flex items-center gap-1 text-xs text-amber-600"><BadgeCheck className="w-3.5 h-3.5" />{offer.seller_rating} seller rating</span>
                </p>
                <p className="flex items-center gap-2 text-slate-600">
                  <Truck className="w-4 h-4 text-slate-400" />
                  Delivery in {offer.delivery_days} day{offer.delivery_days > 1 ? "s" : ""}
                  {offer.shipping_fee > 0 ? ` · ${inr(offer.shipping_fee)} shipping` : " · Free shipping"}
                </p>
                <p className={`flex items-center gap-2 text-sm font-semibold ${offer.stock < 5 ? "text-red-500" : "text-emerald-600"}`}>
                  {offer.stock < 5 ? `Only ${offer.stock} left in stock` : "In stock"}
                </p>
              </div>
              <div className="flex gap-3 mt-6">
                <Button data-testid="add-to-cart-btn" onClick={() => addToCart(false)} variant="outline" className="flex-1 rounded-full h-12 font-semibold border-slate-300">
                  Add to Cart
                </Button>
                <Button data-testid="buy-now-btn" onClick={() => addToCart(true)} className="flex-1 rounded-full h-12 font-semibold bg-slate-900 hover:bg-slate-700">
                  Buy Now
                </Button>
              </div>
              <div className="flex gap-4 mt-4">
                <button data-testid="wishlist-btn" onClick={toggleWish} className={`inline-flex items-center gap-1.5 text-xs font-semibold ${wished ? "text-red-500" : "text-slate-500 hover:text-slate-900"}`}>
                  <Heart className={`w-4 h-4 ${wished ? "fill-red-500" : ""}`} /> Save
                </button>
                <button data-testid="share-btn" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900">
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="out-of-stock" className="mt-6 rounded-2xl border border-slate-200 p-6 text-slate-500 text-sm">Currently unavailable from all sellers.</div>
          )}

          {product.offers?.length > 1 && (
            <div data-testid="other-sellers" className="mt-6">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">{product.offers.length} offers from sellers</p>
              <div className="space-y-2">
                {product.offers.map((o, i) => (
                  <button
                    key={o.id}
                    data-testid={`offer-${o.id}`}
                    onClick={() => setSelectedOffer(o)}
                    className={`w-full flex items-center justify-between gap-4 rounded-xl border p-4 text-left transition-colors ${offer?.id === o.id ? "border-blue-500 bg-blue-50/50" : "border-slate-200 hover:border-slate-400"}`}
                  >
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-2">
                        {o.seller_name}
                        {i === 0 && <span className="text-[10px] uppercase tracking-widest font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Featured offer</span>}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{o.delivery_days}-day delivery · {o.shipping_fee ? inr(o.shipping_fee) : "Free"} shipping · {o.seller_rating} seller rating</p>
                    </div>
                    <span className="font-heading font-bold">{inr(o.price)}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Featured offer balances price, delivery speed and seller reliability — not just the lowest price.</p>
            </div>
          )}

          {user?.role === "seller" && !product.offers?.some((o) => user.seller && o.seller_name === user.seller.business_name) && (
            <SellThisItem productId={product.id} onDone={load} />
          )}

          <Accordion type="single" collapsible className="mt-8">
            <AccordionItem value="desc">
              <AccordionTrigger data-testid="accordion-description">Description</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed">{product.description || "No description provided."}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="specs">
              <AccordionTrigger data-testid="accordion-specs">Specifications</AccordionTrigger>
              <AccordionContent>
                <dl className="text-sm divide-y divide-slate-100">
                  {Object.entries(product.specs || {}).map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 py-2.5">
                      <dt className="text-slate-400">{k}</dt>
                      <dd className="col-span-2 font-medium">{v}</dd>
                    </div>
                  ))}
                  {!Object.keys(product.specs || {}).length && <p className="text-slate-400 text-sm">No specifications listed.</p>}
                </dl>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <section data-testid="reviews-section" className="mt-10">
            <h2 className="font-heading text-xl font-bold">Customer reviews</h2>
            <div className="mt-6 space-y-6">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-slate-100 pb-6">
                  <div className="flex items-center gap-2">
                    <Stars rating={r.rating} />
                    {r.verified_purchase && <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Verified purchase</span>}
                  </div>
                  <p className="font-semibold text-sm mt-2">{r.title}</p>
                  <p className="text-sm text-slate-600 mt-1">{r.text}</p>
                  <p className="text-xs text-slate-400 mt-2">{r.user_name} · {new Date(r.created_at).toLocaleDateString("en-IN")} · {r.helpful} found helpful</p>
                </div>
              ))}
              {!reviews.length && <p className="text-sm text-slate-400">No reviews yet. Be the first to review.</p>}
            </div>
            {user && (
              <form onSubmit={submitReview} className="mt-8 rounded-2xl bg-slate-50 p-6 space-y-4" data-testid="review-form">
                <p className="font-heading font-bold">Write a review</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((r) => (
                    <button type="button" key={r} data-testid={`review-star-${r}`} onClick={() => setReviewForm({ ...reviewForm, rating: r })}>
                      <Stars rating={r <= reviewForm.rating ? r : 0} className="w-5 h-5" />
                    </button>
                  ))}
                </div>
                <input data-testid="review-title-input" value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                  placeholder="Review headline" className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-500" />
                <Textarea data-testid="review-text-input" value={reviewForm.text} onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })} placeholder="What did you like or dislike?" />
                <Button data-testid="review-submit-btn" type="submit" className="rounded-full bg-slate-900">Submit review</Button>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function SellThisItem({ productId, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sku: "", price: "", stock: 10, shipping_fee: 0, delivery_days: 4 });

  const submit = async () => {
    try {
      await api.post(`/products/${productId}/offers`, { ...form, price: Number(form.price), stock: Number(form.stock), shipping_fee: Number(form.shipping_fee), delivery_days: Number(form.delivery_days) });
      toast.success("Your offer is live");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  if (!open) {
    return (
      <Button data-testid="sell-this-item-btn" variant="outline" className="mt-6 rounded-full" onClick={() => setOpen(true)}>
        <Store className="w-4 h-4 mr-2" /> Sell this item
      </Button>
    );
  }
  return (
    <div data-testid="sell-this-item-form" className="mt-6 rounded-2xl border border-slate-200 p-6 space-y-3">
      <p className="font-heading font-bold text-sm">Your offer on this product</p>
      {[
        ["sku", "SKU", "text"], ["price", "Your price (₹)", "number"], ["stock", "Stock", "number"],
        ["shipping_fee", "Shipping fee (₹)", "number"], ["delivery_days", "Delivery days", "number"],
      ].map(([k, label, type]) => (
        <input key={k} data-testid={`offer-form-${k}`} type={type} placeholder={label} value={form[k]}
          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
      ))}
      <Button data-testid="offer-form-submit" onClick={submit} className="rounded-full bg-slate-900">Publish offer</Button>
    </div>
  );
}
